/**
 * Compliance → Notices - Phase 8 batch B item 1 (2026-05-16).
 *
 * Notice generator wizard. Operator fills jurisdiction-specific fields →
 * the controller renders a SHA-256-signed HTML notice → operator can
 * publish (mints a public slug) or keep as draft.
 *
 * Two columns: list of existing notices (left) + create wizard (right).
 * Preview of a selected notice opens in a modal with raw HTML + digest.
 */
import { useCallback, useEffect, useState } from 'react';
import { motion } from 'motion/react';
import {
  FileText,
  Plus,
  Loader2,
  AlertTriangle,
  RefreshCw,
  CheckCircle2,
  Clock,
  XCircle,
  Eye,
  Send,
  Ban,
  ShieldCheck,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/primitives';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { KpiCard } from '@/components/viz/KpiCard';
import {
  listComplianceNotices,
  createComplianceNotice,
  publishComplianceNotice,
  revokeComplianceNotice,
  type ComplianceNotice,
  type NoticeJurisdiction,
  type NoticeStatus,
  type RegulationId,
} from '@/lib/api';

const STATUS_VARIANT: Record<NoticeStatus, 'success' | 'warning' | 'critical' | 'glass'> = {
  draft: 'glass',
  published: 'success',
  expired: 'warning',
  revoked: 'critical',
};
const STATUS_ICON: Record<NoticeStatus, typeof CheckCircle2> = {
  draft: Clock,
  published: CheckCircle2,
  expired: AlertTriangle,
  revoked: XCircle,
};

const REGULATION_OPTIONS: ReadonlyArray<{ value: RegulationId; label: string }> = [
  { value: 'DPDPA_2023', label: 'DPDPA 2023 (India)' },
  { value: 'GDPR', label: 'GDPR Art 13-14 (EU)' },
  { value: 'CCPA', label: 'CCPA notice at collection (US-CA)' },
];

const JURISDICTION_OPTIONS: ReadonlyArray<{ value: NoticeJurisdiction; label: string }> = [
  { value: 'India', label: 'India' },
  { value: 'EU', label: 'EU' },
  { value: 'US-CA', label: 'United States - California' },
  { value: 'UK', label: 'United Kingdom' },
  { value: 'global', label: 'Global' },
];

export function ComplianceNotices(): JSX.Element {
  const [notices, setNotices] = useState<ComplianceNotice[]>([]);
  const [counts, setCounts] = useState({ draft: 0, published: 0, expired: 0, revoked: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create form
  const [form, setForm] = useState({
    regulationId: 'DPDPA_2023' as RegulationId,
    jurisdiction: 'India' as NoticeJurisdiction,
    processingActivity: '',
    dataClassesRaw: '',
    legalBasesRaw: 'consent',
    retentionPeriodDays: 365,
    crossBorderRaw: '',
    contactDpoEmail: '',
    publish: false,
  });
  const [creating, setCreating] = useState(false);

  // Preview modal
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [previewNotice, setPreviewNotice] = useState<ComplianceNotice | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const r = await listComplianceNotices({});
      setNotices(r.notices);
      setCounts(r.byStatus);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load notices');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  async function handleCreate(): Promise<void> {
    if (!form.processingActivity.trim() || !form.contactDpoEmail.trim()) {
      setError('Processing activity and DPO email are required');
      return;
    }
    const dataClasses = form.dataClassesRaw.split(',').map((s) => s.trim()).filter(Boolean);
    const legalBases = form.legalBasesRaw.split(',').map((s) => s.trim()).filter(Boolean);
    const crossBorder = form.crossBorderRaw.split(',').map((s) => s.trim()).filter(Boolean);
    if (dataClasses.length === 0 || legalBases.length === 0) {
      setError('Data classes and legal bases are required');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      await createComplianceNotice({
        regulationId: form.regulationId,
        jurisdiction: form.jurisdiction,
        processingActivity: form.processingActivity.trim(),
        dataClasses,
        legalBases,
        retentionPeriodDays: form.retentionPeriodDays > 0 ? form.retentionPeriodDays : undefined,
        crossBorderDestinations: crossBorder,
        contactDpoEmail: form.contactDpoEmail.trim(),
        publish: form.publish,
      });
      setForm({
        ...form,
        processingActivity: '',
        dataClassesRaw: '',
        crossBorderRaw: '',
        publish: false,
      });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to create notice');
    } finally {
      setCreating(false);
    }
  }

  async function handlePublish(id: string): Promise<void> {
    const slug = window.prompt('Choose a URL slug (lowercase, dashes only):');
    if (!slug || !/^[a-z0-9-]{3,80}$/.test(slug)) return;
    try {
      await publishComplianceNotice(id, { publicSlug: slug });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'publish failed');
    }
  }

  async function handleRevoke(id: string): Promise<void> {
    const reason = window.prompt('Revocation reason:');
    if (!reason) return;
    try {
      await revokeComplianceNotice(id, reason);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'revoke failed');
    }
  }

  function openPreview(n: ComplianceNotice) {
    setPreviewId(n.id);
    setPreviewNotice(n);
  }

  return (
    <PageContainer>
      <PageHeader
        title="Privacy notices"
        subtitle="Generate DPDPA §6 / GDPR Art 13-14 / CCPA notices. Each is SHA-256 signed and tamper-evident - the digest goes into the audit chain."
        badges={[
          { label: 'Template v1.0', variant: 'glass' },
          { label: 'SHA-256 signed', variant: 'glass' },
          ...(loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : error
              ? [{ label: 'Load error', variant: 'warning' as const }]
              : [{ label: 'Live', variant: 'success' as const }]),
        ]}
        actions={
          <Button size="sm" variant="glass" onClick={() => void refresh()} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        }
      />

      {error && (
        <ErrorState
          title="Notice action failed"
          detail={error}
          onRetry={() => setError(null)}
          retryLabel="Dismiss"
        />
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Published" value={counts.published} icon={CheckCircle2} glow={counts.published > 0 ? 'high' : 'low'} hint="live" />
        <KpiCard label="Drafts" value={counts.draft} icon={Clock} glow={counts.draft > 0 ? 'medium' : 'low'} hint="awaiting review" />
        <KpiCard label="Expired" value={counts.expired} icon={AlertTriangle} glow={counts.expired > 0 ? 'medium' : 'low'} hint="renew" />
        <KpiCard label="Revoked" value={counts.revoked} icon={XCircle} hint="archived" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Create wizard */}
        <GlassCard className="p-5">
          <CardHeader className="p-0 mb-3">
            <CardTitle className="flex items-center gap-2">
              <Plus className="h-4 w-4 text-primary" />
              Generate a notice
            </CardTitle>
            <CardDescription clamp>
              Fill the placeholders; the controller renders a jurisdiction-specific HTML body and
              SHA-256-signs it before persistence.
            </CardDescription>
          </CardHeader>
          <div className="space-y-3 text-xs">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-muted-foreground block mb-0.5">Regulation</label>
                <select
                  value={form.regulationId}
                  onChange={(e) => setForm({ ...form, regulationId: e.target.value as RegulationId })}
                  className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
                >
                  {REGULATION_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-muted-foreground block mb-0.5">Jurisdiction</label>
                <select
                  value={form.jurisdiction}
                  onChange={(e) => setForm({ ...form, jurisdiction: e.target.value as NoticeJurisdiction })}
                  className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
                >
                  {JURISDICTION_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="text-muted-foreground block mb-0.5">Processing activity</label>
              <input
                type="text"
                value={form.processingActivity}
                onChange={(e) => setForm({ ...form, processingActivity: e.target.value })}
                placeholder="customer-onboarding-v2"
                maxLength={200}
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
              />
            </div>
            <div>
              <label className="text-muted-foreground block mb-0.5">Data classes (comma-separated)</label>
              <input
                type="text"
                value={form.dataClassesRaw}
                onChange={(e) => setForm({ ...form, dataClassesRaw: e.target.value })}
                placeholder="name, email, aadhaar, phone"
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
              />
            </div>
            <div>
              <label className="text-muted-foreground block mb-0.5">Legal bases (comma-separated)</label>
              <input
                type="text"
                value={form.legalBasesRaw}
                onChange={(e) => setForm({ ...form, legalBasesRaw: e.target.value })}
                placeholder="consent, contract, legitimate interest"
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-muted-foreground block mb-0.5">Retention (days)</label>
                <input
                  type="number"
                  min={1}
                  max={365 * 30}
                  value={form.retentionPeriodDays}
                  onChange={(e) => setForm({ ...form, retentionPeriodDays: Number(e.target.value) })}
                  className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
                />
              </div>
              <div>
                <label className="text-muted-foreground block mb-0.5">Cross-border destinations</label>
                <input
                  type="text"
                  value={form.crossBorderRaw}
                  onChange={(e) => setForm({ ...form, crossBorderRaw: e.target.value })}
                  placeholder="United States, Singapore"
                  className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
                />
              </div>
            </div>
            <div>
              <label className="text-muted-foreground block mb-0.5">DPO contact email</label>
              <input
                type="email"
                value={form.contactDpoEmail}
                onChange={(e) => setForm({ ...form, contactDpoEmail: e.target.value })}
                placeholder="dpo@example.com"
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
              />
            </div>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={form.publish}
                onChange={(e) => setForm({ ...form, publish: e.target.checked })}
              />
              Publish immediately (mints public slug + sets publishedAt)
            </label>
            <div className="flex justify-end pt-1">
              <Button size="sm" data-action="disc.comp_notices.a01" onClick={() => void handleCreate()} disabled={creating}>
                {creating
                  ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  : <FileText className="h-3.5 w-3.5 mr-1" />}
                Generate notice
              </Button>
            </div>
          </div>
        </GlassCard>

        {/* List */}
        <GlassCard className="p-5">
          <CardHeader className="p-0 mb-3">
            <CardTitle>Existing notices</CardTitle>
            <CardDescription clamp>Click a row to preview the signed HTML body.</CardDescription>
          </CardHeader>
          {loading && (
            <LoadingState caption="Loading…" />
          )}
          {!loading && notices.length === 0 && (
            <div className="glass-lite rounded p-6 text-center text-xs text-muted-foreground">
              <FileText className="h-6 w-6 mx-auto mb-2 opacity-40" />
              <div>No notices yet. Generate your first one in the panel on the left.</div>
            </div>
          )}
          {!loading && notices.length > 0 && (
            <div className="space-y-2">
              {notices.map((n, i) => {
                const SIcon = STATUS_ICON[n.status];
                return (
                  <motion.div
                    key={n.id}
                    initial={{ opacity: 0, x: -4 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: Math.min(i * 0.03, 0.3) }}
                    className="glass-lite rounded p-3 text-xs"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">{n.processingActivity}</div>
                        <div className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                          <Badge variant="glass" className="text-[10px] font-mono">{n.regulationId}</Badge>
                          <Badge variant={STATUS_VARIANT[n.status]} className="text-[10px] inline-flex items-center gap-1">
                            <SIcon className="h-3 w-3" />{n.status}
                          </Badge>
                          {n.publicSlug && <code className="font-mono">/{n.publicSlug}</code>}
                          <span>· {new Date(n.createdAt).toLocaleDateString()}</span>
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-1 truncate" title={n.signedDigest}>
                          digest: <code className="font-mono">{n.signedDigest.slice(0, 16)}…</code>
                        </div>
                      </div>
                      <div className="flex flex-col gap-1 shrink-0">
                        <Button size="sm" variant="glass" onClick={() => openPreview(n)} title="Preview">
                          <Eye className="h-3 w-3" />
                        </Button>
                        {n.status === 'draft' && (
                          <Button size="sm" variant="glass" data-action="disc.comp_notices.a02" onClick={() => void handlePublish(n.id)} title="Publish">
                            <Send className="h-3 w-3" />
                          </Button>
                        )}
                        {n.status === 'published' && (
                          <Button size="sm" variant="glass" data-action="disc.comp_notices.a03" onClick={() => void handleRevoke(n.id)} title="Revoke">
                            <Ban className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </GlassCard>
      </div>

      {/* Preview modal */}
      <Dialog open={previewId !== null} onOpenChange={(o) => { if (!o) { setPreviewId(null); setPreviewNotice(null); } }}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-primary" />
              Notice preview
            </DialogTitle>
          </DialogHeader>
          {previewNotice && (
            <div className="space-y-3">
              <div className="text-xs text-muted-foreground">
                Regulation <code className="font-mono">{previewNotice.regulationId}</code> · template
                v{previewNotice.templateVersion} · digest{' '}
                <code className="font-mono">{previewNotice.signedDigest.slice(0, 16)}…</code>
              </div>
              <iframe
                className="w-full h-[500px] rounded border border-border/30 bg-white"
                sandbox=""
                srcDoc={previewNotice.bodyHtml}
                title="Notice preview"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
